import React from 'react';
import Spinner from '@atlaskit/spinner';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives';
import { ButtonProps, ButtonSize } from '../index';
import { buttonIconContainerStyles, disabledIconStyles } from '../styles/sharedButtonStyles';
import {
  getBasicStyles,
  getIconSpacingStyles,
  getResponsiveStyles,
  getAppearanceStyles,
  getStateStyles,
} from '../utils/buttonStyleHelpers';

interface PrimarySecondaryButtonProps extends ButtonProps {
  clickHandler: (e: React.MouseEvent<HTMLElement>) => void;
  testId: string;
  showBeforeIcon: React.ReactElement | false;
  showAfterIcon: React.ReactElement | false;
  isButtonWithoutIcon: boolean;
  isResponsiveSpacing: boolean;
  as?: 'button' | 'a';
  type?: 'button' | 'submit' | 'reset';
}

export const PrimarySecondaryButton: React.FC<PrimarySecondaryButtonProps> = ({
  label,
  appearance,
  disabled = false,
  isLoading = false,
  clickHandler,
  testId,
  href,
  target,
  fullWidth = false,
  selected = false,
  size = ButtonSize.MEDIUM,
  showBeforeIcon,
  showAfterIcon,
  isButtonWithoutIcon,
  isResponsiveSpacing,
  as = 'a',
  type,
}) => {
  const commonProps = {
    'aria-label': label.replaceAll('-', ' '),
    onClick: disabled || isLoading ? undefined : clickHandler,
    role: as === 'button' ? undefined : href ? undefined : 'button',
    tabIndex: disabled || isLoading ? -1 : 0,
    'data-testid': testId,
    css: [
      ...getBasicStyles(appearance!, size),
      ...getIconSpacingStyles(size, isButtonWithoutIcon, showBeforeIcon, showAfterIcon),
      ...getResponsiveStyles(isResponsiveSpacing, size, showBeforeIcon, showAfterIcon),
      getAppearanceStyles(appearance!),
      ...getStateStyles(selected, isLoading, disabled, fullWidth),
    ],
  };

  const children = (
    <>
      {isLoading && <Spinner testId="spinner" size="small" />}
      {showBeforeIcon && (
        <span
          css={[
            buttonIconContainerStyles,
            label && css({ marginRight: token('space.050'), [media.below.md]: { marginRight: 0 } }),
          ]}
        >
          {showBeforeIcon}
        </span>
      )}
      <span css={isLoading && css({ marginLeft: token('space.100') })}>{label}</span>
      {showAfterIcon && (
        <span
          css={[buttonIconContainerStyles, css({ marginLeft: token('space.050') }), disabled && disabledIconStyles]}
        >
          {showAfterIcon}
        </span>
      )}
    </>
  );

  if (as === 'button') {
    return (
      <button {...commonProps} type={type} disabled={disabled || isLoading}>
        {children}
      </button>
    );
  }

  return (
    <a
      {...commonProps}
      href={!disabled && !isLoading ? href : undefined}
      target={target}
      rel={target === '_blank' ? 'noreferrer' : ''}
    >
      {children}
    </a>
  );
};
